import supabase from '../../../../utils/supabase';
// @ts-ignore - JS module
import { WSClient } from '../../../../goServerAPI/apiBase/wsClient.js';
import { useChecklistStore } from '../../../../stores/checklistStore';
import { useProfileSettingsStore } from '../../../../stores/profileSettingsStore';
import { startAutoDisconnectTimer, clearAutoDisconnectTimer } from './autoDisconnect';
import { logService } from '../../../../services/logService';
import {
  callService,
  callTranscriptMessageService,
  callToneSuggestionService,
  callSummaryService,
  callWhatToSaySuggestionService,
} from './services';
import { saveCostToSupabase, nonEmptyString, extractWhatToResponseText, hasRenderableWhatToResponse } from './utils';
import { registerStopOnUnload, unregisterStopOnUnload } from './unloadGuard';
import { setSummarizerWSClient } from './summarizerActions';

export const createCallLifecycle = (set: any, get: any) => ({
  checkActiveCall: async () => {
    const { sdk } = get();
    if (!sdk) return; // return void

    try {
      // Manual fetch because it's a new endpoint not yet in SDK types
      // Assuming sdk._cfg.baseURL is available or constructing path manually
      const baseUrl = sdk._cfg?.baseURL || 'http://localhost:8080';

      // Add timeout to prevent hanging when server is down
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000); // 2 second timeout

      try {
        const res = await fetch(`${baseUrl}/sales/active-call`, {
          signal: controller.signal
        });
        clearTimeout(timeoutId);

        if (!res.ok) return; // return void

        const data = await res.json();
        if (data.isActive && data.callId) {
          console.info('[SALES] Found active call on server, recovering state:', data);

          set((state: any) => ({
            salesCallStatus: {
              ...state.salesCallStatus,
              isActive: true,
              isPaused: typeof data.isPaused === 'boolean' ? data.isPaused : state.salesCallStatus.isPaused,
              callId: data.callId,
              salesmanMicId: data.salesMic,
              clientMicId: data.clientMic,
              startTime: data.startTime ? new Date(data.startTime) : state.salesCallStatus.startTime,
              pauseStartedAt: typeof data.pauseStartedAt === 'string'
                ? data.pauseStartedAt
                : (typeof data.isPaused === 'boolean' && !data.isPaused ? null : state.salesCallStatus.pauseStartedAt),
              accumulatedPauseMs: typeof data.accumulatedPauseMs === 'number'
                ? data.accumulatedPauseMs
                : state.salesCallStatus.accumulatedPauseMs,
              callMode: data.mode === 'simulator' ? 'simulator' : state.salesCallStatus.callMode,
              simulatorStatus: data.mode === 'simulator' ? 'connected' : state.salesCallStatus.simulatorStatus,
            },
            // Restore connected state
            transcriptionStatus: {
              ...state.transcriptionStatus,
              isActive: true
            }
          }));

          // IMPORTANT: Restore log context
          logService.setCallContext(data.callId);
          registerStopOnUnload(get);
          {
            const st = get().salesCallStatus.startTime;
            startAutoDisconnectTimer(
              () => get().salesCallStatus?.isActive,
              get().stopSalesCall,
              st ?? (data.startTime ? new Date(data.startTime) : null)
            );
          }
          return; // return void
        }
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          console.warn('[SALES] checkActiveCall timed out. Server likely unreachable.');
        } else {
          // Re-throw to be caught by outer catch
          throw fetchError;
        }
      }
    } catch (e) {
      console.error('[SALES] Failed to check active call:', e);
    }
    // If we reach here, no active call found or error occurred.
    // If we were expecting an active call (e.g. from persisted state), we might want to clear it here,
    // but the periodic connection check should handle the "disconnected" state.
    // However, if server is down, we definitely shouldn't be "active".

    // Optional: Could enforce cleanup here if fetch failed significantly
  },

  startSalesCall: async (salesmanMicId: string, clientMicId: string, userId?: string | null, centerType?: string) => {
    const { sdk, checkActiveCall, resetConversationData, salesLoading } = get();
    if (!sdk) {
      console.error('[SALES] startSalesCall aborted: Go server SDK is not initialized yet');
      return;
    }
    // Reject concurrent invocations - prevents two listeners (e.g. Voicenter ANSWER + manual click)
    // from racing past the isActive check below before isActive flips to true.
    if (salesLoading) {
      console.warn('[SALES] startSalesCall ignored: another start is already in progress');
      return;
    }
    if (get().salesCallStatus.isActive) {
      console.warn('[SALES] startSalesCall ignored: call already active');
      return;
    }
    // Mark loading immediately so subsequent concurrent calls bail out at the guard above.
    set({ salesLoading: true, salesError: null });

    const { callMode, simulatorDifficulty, simulatorVoice } = get().salesCallStatus;

    // Check for active call first
    try {
      await checkActiveCall();
      // Since checkActiveCall now returns void, we check the store state directly
      if (get().salesCallStatus.isActive) {
        console.warn('[SALES] Call already active on server. Resumed session.');
        startAutoDisconnectTimer(
          () => get().salesCallStatus?.isActive,
          get().stopSalesCall,
          get().salesCallStatus.startTime ?? undefined
        );
        set({ salesLoading: false });
        return;
      }
    } catch (e) { /* ignore */ }

    // Reset all conversation data before starting a new call (only if not resuming)
    resetConversationData();

    try {
      // 1. Create Call entity first to get ID for logging
      let finalUserId: string | null = userId || null;
      if (!finalUserId) {
        try {
          const { data } = await supabase.auth.getUser();
          finalUserId = data?.user?.id || null;
        } catch (authErr) {
          console.error('[PERSIST] Failed to get user for call start:', authErr);
        }
      }

      const call = await callService.startCall(finalUserId);
      const callId = call?.id || null;
      // Ensure we have a callId for the backend and logging, even if DB creation failed
      const effectiveCallId = callId || 'temporary-id-' + Date.now();

      if (callId) {
        set((state: any) => ({
          salesCallStatus: {
            ...state.salesCallStatus,
            callId: callId,
          }
        }));
        console.info('[PERSIST] Call started', { callId, userId: finalUserId });
      } else {
        console.error('[PERSIST] Call start returned no id; using temporary ID: ' + effectiveCallId);
      }

      // Set log context for browser logging using the actual ID sent to backend
      logService.setCallContext(effectiveCallId);

      // 2. Start call on Go server with the callId and userId
      const checklistState = useChecklistStore.getState();
      const centerDescription = (checklistState.centerDescription || '').trim();

      // Snapshot the user's mic-gate threshold at dispatch time so it travels
      // with the call. Default -48 mirrors the Go server's fallback.
      const micMinDbFS = useProfileSettingsStore.getState().micMinDbFS ?? -48;

      if (callMode === 'simulator') {
        if (!centerDescription) {
          throw new Error('Business info (center description) is required for simulator mode');
        }

        await sdk.sales.startSimulatorCall({
          callId: effectiveCallId,
          userId: finalUserId || undefined,
          salesmanMicId,
          clientPlaybackDeviceId: clientMicId,
          businessInfo: centerDescription,
          difficultyLevel: simulatorDifficulty || 'medium',
          voice: simulatorVoice || 'Rex',
          centerType: centerType,
          micMinDbFS,
        });
      } else {
        await sdk.sales.startSaleCall({
          salesmanMicId,
          clientMicId,
          callId: effectiveCallId,
          userId: finalUserId || undefined,
          centerType: centerType,
          micMinDbFS,
        });
      }

      try {
        if (callId) {
          // Start checklist detector with questions from checklistStore
          try {
            console.log('[CHECKLIST] 🔍 Getting checklistStore state...');
            const checklistState = useChecklistStore.getState();
            console.log('[CHECKLIST] 📋 ChecklistStore state:', {
              questionsCount: checklistState.questions?.length || 0,
              centerDescription: checklistState.centerDescription?.substring(0, 50) || '(empty)',
              loading: checklistState.loading,
              error: checklistState.error
            });

            const questions = checklistState.questions || [];
            const centerDescription = checklistState.centerDescription || '';

            if (questions.length > 0) {
              console.log('[CHECKLIST] 🚀 Starting checklist detector with', questions.length, 'questions');
              console.log('[CHECKLIST] 📝 Questions:', questions.map((q: any) => q.text).slice(0, 3));

              const requestPayload = {
                callId: callId,
                businessInfo: centerDescription,
                questions: questions.map((q: any, idx: number) => ({
                  id: q.id,
                  index: idx + 1,
                  text: q.text,
                  category_id: q.category_id || '',
                  is_done: false
                }))
              };
              console.log('[CHECKLIST] 📤 Request payload:', requestPayload);

              await sdk.sales.startChecklistDetector(requestPayload);
              console.log('[CHECKLIST] ✅ Checklist detector started successfully');
            } else {
              console.log('[CHECKLIST] ⚠️ No questions configured, skipping detector start');
            }
          } catch (checklistErr) {
            console.error('[CHECKLIST] ❌ Failed to start checklist detector:', checklistErr);
          }
        }
      } catch (e) {
        console.error('[PERSIST] Failed to create Call entity:', e);
      }

      const agentConnections = {
        isResponseNeeded: false,
        salesStage: false,
        speechTone: false,
        summarizer: false,
        whatToResponse: false,
      };

      const agentData = {
        isResponseNeeded: null,
        salesStage: null,
        speechTone: null,
        summarizer: null,
        whatToResponse: null,
        whatToResponseHistory: [],
        fullTranscriptResult: null,
        shortSummaryResult: null,
        feedbackResult: null,
        feedbackCallId: null,
        isFeedbackStreaming: false,
        summaryResult: null,
        whatsappResult: null,
        analysisResult: null,
        isSummaryStreaming: false,
        isWhatsappStreaming: false,
        isAnalysisStreaming: false,
        isAnalysisRequested: false,
        callDurationSeconds: null,
      };

      // Connect to IsResponseNeeded agent
      try {
        sdk.sales.wsIsResponseNeeded({
          onMessage: (data: any) => {
            // Log cost information if available
            if (data.cost) {
              console.log(`[COST] IsResponseNeeded Agent:`, {
                agent: 'IsResponseNeeded',
                inputTokens: data.cost.input_tokens,
                outputTokens: data.cost.output_tokens,
                inputCost: `$${data.cost.input_cost.toFixed(6)}`,
                outputCost: `$${data.cost.output_cost.toFixed(6)}`,
                totalCost: `$${data.cost.total_cost.toFixed(6)}`,
                model: data.cost.model,
                pricing: data.cost.pricing_info ? {
                  model: data.cost.pricing_info.model,
                  inputRate: data.cost.pricing_info.input_rate,
                  outputRate: data.cost.pricing_info.output_rate,
                } : null,
                timestamp: new Date(data.cost.timestamp).toISOString(),
              });

              // Save cost data to Supabase
              const currentCallId = get().salesCallStatus.callId;
              saveCostToSupabase(data.cost, 'IsResponseNeeded', currentCallId).catch(console.error);
            }

            set((state: any) => ({
              salesCallStatus: {
                ...state.salesCallStatus,
                agentData: {
                  ...state.salesCallStatus.agentData,
                  isResponseNeeded: data,
                },
              },
            }));
          },
          onOpen: () => {
            set((state: any) => ({
              salesCallStatus: {
                ...state.salesCallStatus,
                agentConnections: {
                  ...state.salesCallStatus.agentConnections,
                  isResponseNeeded: true,
                },
              },
            }));
          },
          onClose: () => {
            set((state: any) => ({
              salesCallStatus: {
                ...state.salesCallStatus,
                agentConnections: {
                  ...state.salesCallStatus.agentConnections,
                  isResponseNeeded: false,
                },
              },
            }));
          },
        });
      } catch (error) {
        console.error('[SALES] Failed to connect to IsResponseNeeded agent:', error);
      }

      // Connect to SalesStage agent
      try {
        sdk.sales.wsSalesStage({
          onMessage: (data: any) => {
            // Log cost information if available
            if (data.cost) {
              console.log(`[COST] SalesStage Agent:`, {
                agent: 'SalesStage',
                inputTokens: data.cost.input_tokens,
                outputTokens: data.cost.output_tokens,
                inputCost: `$${data.cost.input_cost.toFixed(6)}`,
                outputCost: `$${data.cost.output_cost.toFixed(6)}`,
                totalCost: `$${data.cost.total_cost.toFixed(6)}`,
                model: data.cost.model,
                pricing: data.cost.pricing_info ? {
                  model: data.cost.pricing_info.model,
                  inputRate: data.cost.pricing_info.input_rate,
                  outputRate: data.cost.pricing_info.output_rate,
                } : null,
                timestamp: new Date(data.cost.timestamp).toISOString(),
              });

              // Save cost data to Supabase
              const currentCallId = get().salesCallStatus.callId;
              saveCostToSupabase(data.cost, 'SalesStage', currentCallId).catch(console.error);
            }

            set((state: any) => ({
              salesCallStatus: {
                ...state.salesCallStatus,
                agentData: {
                  ...state.salesCallStatus.agentData,
                  salesStage: data,
                },
              },
              urgentHelpInFlight: false,
            }));
          },
          onOpen: () => {
            set((state: any) => ({
              salesCallStatus: {
                ...state.salesCallStatus,
                agentConnections: {
                  ...state.salesCallStatus.agentConnections,
                  salesStage: true,
                },
              },
            }));
          },
          onClose: () => {
            set((state: any) => ({
              salesCallStatus: {
                ...state.salesCallStatus,
                agentConnections: {
                  ...state.salesCallStatus.agentConnections,
                  salesStage: false,
                },
              },
              urgentHelpInFlight: false,

            }));
          },
        });
      } catch (error) {
        console.error('[SALES] Failed to connect to SalesStage agent:', error);
      }

      // Connect to SpeechTone agent
      try {
        sdk.sales.wsSpeechTone({
          onMessage: (data: any) => {
            // Log cost information if available
            if (data.cost) {
              console.log(`[COST] SpeechTone Agent:`, {
                agent: 'SpeechTone',
                inputTokens: data.cost.input_tokens,
                outputTokens: data.cost.output_tokens,
                inputCost: `$${data.cost.input_cost.toFixed(6)}`,
                outputCost: `$${data.cost.output_cost.toFixed(6)}`,
                totalCost: `$${data.cost.total_cost.toFixed(6)}`,
                model: data.cost.model,
                pricing: data.cost.pricing_info ? {
                  model: data.cost.pricing_info.model,
                  inputRate: data.cost.pricing_info.input_rate,
                  outputRate: data.cost.pricing_info.output_rate,
                } : null,
                timestamp: new Date(data.cost.timestamp).toISOString(),
              });

              // Save cost data to Supabase
              const currentCallId = get().salesCallStatus.callId;
              saveCostToSupabase(data.cost, 'SpeechTone', currentCallId).catch(console.error);
            }

            set((state: any) => ({
              salesCallStatus: {
                ...state.salesCallStatus,
                agentData: {
                  ...state.salesCallStatus.agentData,
                  speechTone: data,
                },
              },
            }));
            const callId = get().salesCallStatus.callId;
            if (!callId) {
              console.warn('[PERSIST] Skipping tone suggestion save: missing callId');
            } else {
              try {
                const nowISO = new Date().toISOString();
                callToneSuggestionService
                  .createSuggestion(callId, (data?.toneText || data?.text || JSON.stringify(data)), nowISO)
                  .then((res) => { if (!res) console.error('[PERSIST] Tone suggestion save returned null', { callId, nowISO }); })
                  .catch((err) => console.error('[PERSIST] Tone suggestion save failed', err));
              } catch (err) {
                console.error('[PERSIST] Tone suggestion persistence threw', err);
              }
            }
          },
          onOpen: () => {
            set((state: any) => ({
              salesCallStatus: {
                ...state.salesCallStatus,
                agentConnections: {
                  ...state.salesCallStatus.agentConnections,
                  speechTone: true,
                },
              },
            }));
          },
          onClose: () => {
            set((state: any) => ({
              salesCallStatus: {
                ...state.salesCallStatus,
                agentConnections: {
                  ...state.salesCallStatus.agentConnections,
                  speechTone: false,
                },
              },
            }));
          },
        });
      } catch (error) {
        console.error('[SALES] Failed to connect to SpeechTone agent:', error);
      }

      // Connect to Summarizer agent
      try {
        console.log('🔍 [SUMMARIZER_STORE] ========== SETTING UP SUMMARIZER WEBSOCKET ==========');
        setSummarizerWSClient(sdk.sales.wsSummarizer({
          onMessage: (data: any) => {
            console.log('📦 [SUMMARIZER_STORE] ========== 📦 PAYLOAD RECEIVED 📦 ==========');
            console.log('📦 [SUMMARIZER_STORE] Raw payload:', data);
            console.log('📦 [SUMMARIZER_STORE] Payload type:', typeof data);
            console.log('📦 [SUMMARIZER_STORE] Payload keys:', Object.keys(data || {}));

            console.log('📝 [SUMMARIZER_STORE] ========== 📝 RESPONSE DATA 📝 ==========');
            console.log('📝 [SUMMARIZER_STORE] Response summary:', {
              type: data.type,
              hasSummary: !!data.summary,
              summaryLength: data.summary?.length || 0,
              summaryPreview: data.summary ? data.summary.substring(0, 100) : null,
              summaryFull: data.summary,
              keyPointsCount: Array.isArray(data.keyPoints) ? data.keyPoints.length : 0,
              keyPoints: data.keyPoints,
              actionItemsCount: Array.isArray(data.actionItems) ? data.actionItems.length : 0,
              actionItems: data.actionItems,
              hasNextStep: !!data.nextStep,
              nextStep: data.nextStep,
              timestamp: data.timestamp,
              cost: data.cost ? '💰 Cost data available' : 'No cost data',
            });
            console.log('📝 [SUMMARIZER_STORE] Full response payload:', JSON.stringify(data, null, 2));

            // Log cost information if available
            if (data.cost) {
              console.log('💰 [SUMMARIZER_STORE] ========== 💰 COST DATA 💰 ==========');
              console.log('💰 [SUMMARIZER_STORE] Cost payload:', {
                agent: 'Summarizer',
                inputTokens: data.cost.input_tokens,
                outputTokens: data.cost.output_tokens,
                inputCost: `$${data.cost.input_cost.toFixed(6)}`,
                outputCost: `$${data.cost.output_cost.toFixed(6)}`,
                totalCost: `$${data.cost.total_cost.toFixed(6)}`,
                model: data.cost.model,
                pricing: data.cost.pricing_info ? {
                  model: data.cost.pricing_info.model,
                  inputRate: data.cost.pricing_info.input_rate,
                  outputRate: data.cost.pricing_info.output_rate,
                } : null,
                timestamp: new Date(data.cost.timestamp).toISOString(),
              });

              // Save cost data to Supabase
              const currentCallId = get().salesCallStatus.callId;
              saveCostToSupabase(data.cost, 'Summarizer', currentCallId).catch(console.error);
            } else {
              console.log('💰 [SUMMARIZER_STORE] No cost data in response');
            }

            if (data.type === 'simulator_status') {
              set((state: any) => ({
                salesCallStatus: {
                  ...state.salesCallStatus,
                  simulatorStatus: data.status || null,
                },
                ...(data.status === 'error' && data.error ? { salesError: data.error } : {}),
              }));
              return;
            }

            // CHECK FOR STOP MESSAGE
            // Backend sends { "type": "info", "message": "sales call stopped" }
            if (data.message === 'sales call stopped' || (data.type === 'info' && data.message === 'sales call stopped')) {
              console.log('🛑 [SUMMARIZER_STORE] Received STOP signal from backend. Stopping call on frontend...');
              // Trigger stop action
              get().stopSalesCall().catch((err: any) => console.error('Failed to stop call from signal:', err));
              return;
            }

            // Normalize data format - ensure camelCase
            // Handle array inputs from backend (new schema)
            let summaryText = '';
            let ammoItemsList: string[] = [];

            if (Array.isArray(data.new_summary)) {
              summaryText = data.new_summary.join('\n\n');
            } else if (typeof data.new_summary === 'string') {
              summaryText = data.new_summary;
            } else if (data.summary) {
              summaryText = data.summary;
            }

            if (Array.isArray(data.new_ammo)) {
              ammoItemsList = data.new_ammo;
            } else if (typeof data.new_ammo === 'string') { // Fallback/Legacy
              ammoItemsList = (data.new_ammo as string).split('***').filter((s: string) => s.trim());
            } else if (data.ammo) {
              ammoItemsList = (data.ammo as string).split('***').filter((s: string) => s.trim());
            }

            // Handle new_feedback: convert strings to objects with timestamps
            let feedbackList: Array<{ text: string, timestamp: string }> = [];

            const rawFeedback = data.new_feedback || [];
            if (Array.isArray(rawFeedback)) {
              feedbackList = rawFeedback.map((item: any) => {
                const ts = data.timestamp || new Date().toISOString();
                if (typeof item === 'string') {
                  return { text: item, timestamp: ts };
                }
                // Already an object? Ensure timestamp exists
                return {
                  text: item.text || JSON.stringify(item),
                  timestamp: item.timestamp || ts
                };
              });
            }

            // Legacy JSON handling (if backend reverts or sends mixed types)
            // ... (Removed valid JSON check complexity if we trust new schema, but keeping simple fallback)

            const normalizedData = {
              ...data,
              summary: summaryText,
              keyPoints: data.keyPoints || data.key_points || [], // These might be deprecated?
              actionItems: data.actionItems || data.action_items || [],
              nextStep: data.nextStep || data.next_step || '',
              new_feedback: feedbackList, // Use processed list
            };

            set((state: any) => {
              const accSummary = state.salesCallStatus.accumulatedSummary;
              const currentSummarizer = state.salesCallStatus.agentData.summarizer;

              // update accumulated summary
              let newAccumulatedSummary = { ...accSummary };
              const incomingSummary = summaryText.trim();
              const timestamp = normalizedData.timestamp || new Date().toISOString();

              // Replace summary state if new data arrived
              if (incomingSummary && incomingSummary !== accSummary.lastProcessedSummaryRef) {
                const newMsgId = accSummary.summaryMessageIdCounter + 1;
                newAccumulatedSummary = {
                  ...newAccumulatedSummary,
                  messages: [
                    ...newAccumulatedSummary.messages,
                    {
                      id: `summary-msg-${newMsgId}`,
                      text: incomingSummary,
                      timestamp,
                    }
                  ], // Append messages
                  lastProcessedSummaryRef: incomingSummary,
                  summaryMessageIdCounter: newMsgId,
                };
              }

              // Replace ammo items
              // Helper to check array equality
              const currentAmmoStr = JSON.stringify(newAccumulatedSummary.ammoItems.map((a: any) => a.text));
              const newAmmoStr = JSON.stringify(ammoItemsList);

              if (ammoItemsList.length > 0 && currentAmmoStr !== newAmmoStr) {
                let ammoCounter = newAccumulatedSummary.ammoIdCounter;
                const newItems = ammoItemsList.map(text => {
                  ammoCounter++;
                  return {
                    id: `ammo-${ammoCounter}`,
                    text: text,
                    timestamp
                  };
                });

                newAccumulatedSummary = {
                  ...newAccumulatedSummary,
                  ammoItems: newItems,
                  lastProcessedAmmoRef: newAmmoStr, // Store signature
                  ammoIdCounter: ammoCounter
                };
              }

              // Handle feedback items (Append new feedback)
              const incomingFeedbackList = normalizedData.new_feedback || [];
              if (incomingFeedbackList.length > 0) {
                let feedbackCounter = newAccumulatedSummary.feedbackIdCounter || 0;
                const newFeedbackItems = incomingFeedbackList.map((item: any) => {
                  feedbackCounter++;
                  return {
                    id: `feedback-${feedbackCounter}-${timestamp}`,
                    text: item.text,
                    timestamp: item.timestamp
                  };
                });

                newAccumulatedSummary = {
                  ...newAccumulatedSummary,
                  feedbackItems: [...(newAccumulatedSummary.feedbackItems || []), ...newFeedbackItems],
                  feedbackIdCounter: feedbackCounter
                };
              }



              // Handle append functionality
              if (normalizedData.append && currentSummarizer) {
                // Append new data to existing data
                const newSummarizer = {
                  ...normalizedData,
                  summary: currentSummarizer.summary ? `${currentSummarizer.summary}\n\n${normalizedData.summary}` : normalizedData.summary,
                  keyPoints: [...(currentSummarizer.keyPoints || []), ...(normalizedData.keyPoints || [])],
                  actionItems: [...(currentSummarizer.actionItems || []), ...(normalizedData.actionItems || [])],
                  nextStep: normalizedData.nextStep || currentSummarizer.nextStep,
                  // Append new feedback to existing list
                  new_feedback: [...(currentSummarizer.new_feedback || []), ...(normalizedData.new_feedback || [])],
                  timestamp: normalizedData.timestamp || currentSummarizer.timestamp,
                };

                // --- NEW LOGIC: Extract WhatToSay from Summarizer ---
                // If the summarizer payload includes whatToSay/HowToSay, we update that state too
                let newWhatToResponseState = state.salesCallStatus.agentData.whatToResponse;
                let newWhatToResponseHistory = state.salesCallStatus.agentData.whatToResponseHistory;

                const whatToSay = normalizedData.whatToSay || normalizedData.what_to_say || normalizedData.WhatToSay;
                const howToSay = normalizedData.HowToSay || normalizedData.how_to_say || normalizedData.howToSay;

                if (whatToSay || howToSay) {
                  console.log('🗣️ [SUMMARIZER_STORE] Found WhatToSay/HowToSay in payload:', { whatToSay, howToSay });

                  const newEntry = {
                    whatToSay: whatToSay,
                    what_to_say: whatToSay,
                    howToSay: howToSay,
                    how_to_say: howToSay,
                    triggered_by: 'Summarizer',
                    timestamp: normalizedData.timestamp || new Date().toISOString(),
                    isStreaming: false,
                  };

                  const currentResp = state.salesCallStatus.agentData.whatToResponse;
                  const isCurrentStreaming = !!currentResp?.isStreaming;
                  if (state.salesCallStatus.manualWhatToSayLock || isCurrentStreaming) {
                    console.log('🔒 [SUMMARIZER_STORE] Skipping automatic WhatToSay update due to manual lock or active stream');
                  } else {
                    newWhatToResponseState = newEntry;
                    newWhatToResponseHistory = [...(newWhatToResponseHistory || []), newEntry];

                    // Persist to database
                    const callId = get().salesCallStatus.callId;
                    if (callId) {
                      try {
                        const replyToSave = JSON.stringify({
                          how_to_say: howToSay || '',
                          what_to_say: whatToSay || ''
                        });
                        callWhatToSaySuggestionService
                          .createWhatToSaySuggestion(callId, 'automatic', replyToSave, newEntry.timestamp)
                          .catch((err) => console.error('[PERSIST] Automatic WhatToSay save failed', err));
                      } catch (err) {
                        console.error('[PERSIST] Automatic WhatToSay persistence threw', err);
                      }
                    }
                  }
                }
                // ----------------------------------------------------

                console.log('➕ [SUMMARIZER_STORE] ========== ➕ APPENDING DATA ➕ ==========');
                console.log('➕ [SUMMARIZER_STORE] New summarizer after append:', newSummarizer);

                return {
                  salesCallStatus: {
                    ...state.salesCallStatus,
                    accumulatedSummary: newAccumulatedSummary,
                    agentData: {
                      ...state.salesCallStatus.agentData,
                      summarizer: newSummarizer,
                      whatToResponse: newWhatToResponseState,
                      whatToResponseHistory: newWhatToResponseHistory,
                    },
                  },
                };
              } else {
                // Replace with new data (default behavior)
                console.log('🔄 [SUMMARIZER_STORE] ========== 🔄 REPLACING DATA 🔄 ==========');
                console.log('🔄 [SUMMARIZER_STORE] Replacing with normalized data:', normalizedData);

                // If replacing, we just use normalizedData.new_feedback which is already the new list

                // --- NEW LOGIC: Extract WhatToSay from Summarizer ---
                let newWhatToResponseState = state.salesCallStatus.agentData.whatToResponse;
                let newWhatToResponseHistory = state.salesCallStatus.agentData.whatToResponseHistory;

                const whatToSay = normalizedData.whatToSay || normalizedData.what_to_say || normalizedData.WhatToSay;
                const howToSay = normalizedData.HowToSay || normalizedData.how_to_say || normalizedData.howToSay;

                if (whatToSay || howToSay) {
                  console.log('🗣️ [SUMMARIZER_STORE] Found WhatToSay/HowToSay in payload (Replace Mode):', { whatToSay, howToSay });

                  const newEntry = {
                    whatToSay: whatToSay,
                    what_to_say: whatToSay,
                    howToSay: howToSay,
                    how_to_say: howToSay,
                    triggered_by: 'Summarizer',
                    timestamp: normalizedData.timestamp || new Date().toISOString(),
                    isStreaming: false,
                  };

                  const currentResp = state.salesCallStatus.agentData.whatToResponse;
                  const isCurrentStreaming = !!currentResp?.isStreaming;
                  if (state.salesCallStatus.manualWhatToSayLock || isCurrentStreaming) {
                    console.log('🔒 [SUMMARIZER_STORE] Skipping automatic WhatToSay update (Replace Mode) due to manual lock or active stream');
                  } else {
                    newWhatToResponseState = newEntry;
                    // In replace mode, do we replace history? Probably just append new entry
                    newWhatToResponseHistory = [...(newWhatToResponseHistory || []), newEntry];

                    // Persist to database
                    const callId = get().salesCallStatus.callId;
                    if (callId) {
                      try {
                        const replyToSave = JSON.stringify({
                          how_to_say: howToSay || '',
                          what_to_say: whatToSay || ''
                        });
                        callWhatToSaySuggestionService
                          .createWhatToSaySuggestion(callId, 'automatic', replyToSave, newEntry.timestamp)
                          .catch((err) => console.error('[PERSIST] Automatic WhatToSay save failed', err));
                      } catch (err) {
                        console.error('[PERSIST] Automatic WhatToSay persistence threw', err);
                      }
                    }
                  }
                }
                // ----------------------------------------------------

                const newState = {
                  salesCallStatus: {
                    ...state.salesCallStatus,
                    accumulatedSummary: newAccumulatedSummary,
                    agentData: {
                      ...state.salesCallStatus.agentData,
                      summarizer: normalizedData,
                      whatToResponse: newWhatToResponseState,
                      whatToResponseHistory: newWhatToResponseHistory,
                    },
                  },
                };
                console.log('✅ [SUMMARIZER_STORE] ========== ✅ STATE UPDATED ✅ ==========');
                console.log('✅ [SUMMARIZER_STORE] New summarizer in state:', newState.salesCallStatus.agentData.summarizer);
                console.log('✅ [SUMMARIZER_STORE] Accumulated messages count:', newAccumulatedSummary.messages.length);
                console.log('✅ [SUMMARIZER_STORE] ==========================================');
                return newState;
              }
            });
            const callId = get().salesCallStatus.callId;
            if (!callId) {
              console.warn('⚠️ [SUMMARIZER_STORE] Skipping summary save: missing callId');
            } else {
              try {
                // Use accumulated ammo (what the dashboard shows) rather than the current
                // message's ammoItemsList — later messages may send ammo == summary (AI overlap),
                // which would corrupt the DB. Accumulated ammo only updates when genuinely different.
                const accumulatedAmmo = get().salesCallStatus.accumulatedSummary?.ammoItems?.map((a: any) => a.text) || [];
                const summaryPayload = {
                  summary: normalizedData?.summary || '',
                  ammo: accumulatedAmmo,
                  keyPoints: normalizedData?.keyPoints || [],
                  actionItems: normalizedData?.actionItems || [],
                  nextStep: normalizedData?.nextStep || '',
                  new_feedback: normalizedData?.new_feedback || [],
                  type: 'summarizer'
                };
                const summaryText = JSON.stringify(summaryPayload);
                const timestamp = new Date().toISOString();
                console.log('💾 [SUMMARIZER_STORE] ========== 💾 SAVING SUMMARY 💾 ==========');
                console.log('💾 [SUMMARIZER_STORE] CallId:', callId);
                console.log('💾 [SUMMARIZER_STORE] payload.summary:', summaryPayload.summary);
                console.log('💾 [SUMMARIZER_STORE] payload.ammo:', summaryPayload.ammo);
                console.log('💾 [SUMMARIZER_STORE] Timestamp:', timestamp);
                callSummaryService
                  .createSummary(callId, summaryText, timestamp)
                  .then((res) => {
                    if (!res) {
                      console.error('❌ [SUMMARIZER_STORE] Summary save returned null', { callId });
                    } else {
                      console.log('✅ [SUMMARIZER_STORE] Summary saved successfully:', res);
                    }
                  })
                  .catch((err) => console.error('❌ [SUMMARIZER_STORE] Summary save failed', err));
              } catch (err) {
                console.error('❌ [SUMMARIZER_STORE] Summary persistence threw', err);
              }
            }
          },
          onOpen: () => {
            console.log('✅ [SUMMARIZER_STORE] ========== ✅ WEBSOCKET OPENED ✅ ==========');
            set((state: any) => ({
              salesCallStatus: {
                ...state.salesCallStatus,
                agentConnections: {
                  ...state.salesCallStatus.agentConnections,
                  summarizer: true,
                },
              },
            }));
          },
          onClose: () => {
            console.log('❌ [SUMMARIZER_STORE] ========== ❌ WEBSOCKET CLOSED ❌ ==========');
            set((state: any) => ({
              salesCallStatus: {
                ...state.salesCallStatus,
                agentConnections: {
                  ...state.salesCallStatus.agentConnections,
                  summarizer: false,
                },
              },
            }));
          },
        }));
      } catch (error) {
        console.error('❌ [SUMMARIZER_STORE] ========== ❌ CONNECTION FAILED ❌ ==========');
        console.error('❌ [SUMMARIZER_STORE] Failed to connect to Summarizer agent:', error);
      }


      // Connect to WhatToResponse agent
      try {
        sdk.sales.wsWhatToResponse({
          onMessage: (data: any) => {
            // Handle streaming chunks
            if ((data.type === 'what_to_response_stream' || data.type === 'objection_handling' || data.is_streaming === true) && data.is_streaming !== false) {
              console.log('[FRONTEND] [STREAM] Received streaming chunk:', {
                type: data.type,
                messageLength: (data.message || data.text)?.length || 0,
                messagePreview: (data.message || data.text)?.substring(0, 50) || '',
                triggeredBy: data.triggered_by || 'Objection Agent',
                timestamp: data.timestamp,
                isStreaming: data.is_streaming
              });

              set((state: any) => {
                const isStreaming = data.is_streaming !== undefined ? data.is_streaming : true;
                const currentWhatToResponse = state.salesCallStatus.agentData.whatToResponse || {};
                const incomingMessage = extractWhatToResponseText(data);
                const currentMessage = extractWhatToResponseText(currentWhatToResponse);

                // Ignore empty stream-start packets only when we have nothing renderable yet.
                if (!incomingMessage && !currentMessage && !hasRenderableWhatToResponse(currentWhatToResponse)) {
                  return state;
                }

                // Construct the new state object
                const newWhatToResponse = {
                  ...currentWhatToResponse,
                  ...(incomingMessage ? {
                    message: incomingMessage,
                    text: incomingMessage,
                    what_to_say: null, // Clear structured fields for text-only responses
                    how_to_say: null,
                  } : {}),
                  triggered_by: data.triggered_by || currentWhatToResponse.triggered_by || (data.type === 'objection_handling' ? 'Objection Agent' : undefined),
                  timestamp: data.timestamp || currentWhatToResponse.timestamp || new Date().toISOString(),
                  isStreaming: isStreaming,
                };

                const newState = {
                  salesCallStatus: {
                    ...state.salesCallStatus,
                    agentData: {
                      ...state.salesCallStatus.agentData,
                      whatToResponse: newWhatToResponse,
                    },
                  },
                };

                // If streaming finished, append to history
                if (!isStreaming && hasRenderableWhatToResponse(newWhatToResponse)) {
                  newState.salesCallStatus.agentData.whatToResponseHistory = [
                    ...(state.salesCallStatus.agentData.whatToResponseHistory || []),
                    {
                      ...newWhatToResponse,
                      isStreaming: false
                    }
                  ];
                  if (data.type === 'objection_handling') {
                    newState.salesCallStatus.manualWhatToSayLock = false;
                    (newState as any).objectionInFlight = false;
                  }
                }

                return newState;
              });
              return;
            }

            // Handle final response
            // Ignore events that have no content (e.g. heartbeat or irrelevant ones)
            const normalizedMessage = extractWhatToResponseText(data);
            const hasStructuredResponse = Boolean(
              nonEmptyString(data.what_to_say) ||
              nonEmptyString(data.WhatToSay) ||
              nonEmptyString(data.how_to_say) ||
              nonEmptyString(data.HowToSay)
            );

            if (!normalizedMessage && !hasStructuredResponse) {
              return;
            }

            // Log cost information if available
            if (data.cost) {
              console.log(`[COST] WhatToResponse Agent:`, {
                agent: 'WhatToResponse',
                inputTokens: data.cost.input_tokens,
                outputTokens: data.cost.output_tokens,
                inputCost: `$${data.cost.input_cost.toFixed(6)}`,
                outputCost: `$${data.cost.output_cost.toFixed(6)}`,
                totalCost: `$${data.cost.total_cost.toFixed(6)}`,
                model: data.cost.model,
                pricing: data.cost.pricing_info ? {
                  model: data.cost.pricing_info.model,
                  inputRate: data.cost.pricing_info.input_rate,
                  outputRate: data.cost.pricing_info.output_rate,
                } : null,
                timestamp: new Date(data.cost.timestamp).toISOString(),
              });

              // Save cost data to Supabase
              const currentCallId = get().salesCallStatus.callId;
              saveCostToSupabase(data.cost, 'WhatToResponse', currentCallId).catch(console.error);
            }

            const inferredTrigger = data.triggered_by ||
              (data.type === 'objection_handling' ? 'Objection Agent' :
                (data.type === 'checklist_question' ? 'Checklist' :
                  (data.type === 'dig_deeper' ? 'Dig Deeper' : 'manual')));

            const normalizedPayload = {
              ...data,
              ...(normalizedMessage ? { message: normalizedMessage, text: normalizedMessage } : {}),
              timestamp: data.timestamp || new Date().toISOString(),
            };

            const isObjectionResponse = data.type === 'objection_handling';

            set((state: any) => ({
              salesCallStatus: {
                ...state.salesCallStatus,
                agentData: {
                  ...state.salesCallStatus.agentData,
                  whatToResponse: {
                    ...normalizedPayload,
                    triggered_by: inferredTrigger,
                    isStreaming: false,
                  },
                  whatToResponseHistory: [
                    ...(state.salesCallStatus.agentData.whatToResponseHistory || []),
                    {
                      ...normalizedPayload,
                      triggered_by: inferredTrigger,
                      isStreaming: false,
                    }
                  ],
                },
                manualWhatToSayLock: isObjectionResponse ? false : state.salesCallStatus.manualWhatToSayLock,
              },
              urgentHelpInFlight: false,
              checklistQuestionInFlight: null,
              digDeeperInFlight: false,
              objectionInFlight: false,
            }));

            // Persist to database
            const callId = get().salesCallStatus.callId;
            const replyRaw = normalizedMessage || data.what_to_say || data.WhatToSay || data.suggestion || '';
            const howToSayRaw = data.how_to_say || data.HowToSay || '';

            if (callId && (replyRaw || howToSayRaw)) {
              try {
                const nowISO = new Date().toISOString();
                const trigger = String(inferredTrigger).trim();

                let replyToSave = String(replyRaw).trim();
                // If the response came with structured how/what, save it as JSON so parseContent can read it
                if (data.what_to_say || data.how_to_say || data.WhatToSay || data.HowToSay) {
                  replyToSave = JSON.stringify({
                    how_to_say: howToSayRaw,
                    what_to_say: replyRaw
                  });
                } else if (!replyToSave && data.text) {
                  replyToSave = String(data.text).trim();
                }

                callWhatToSaySuggestionService
                  .createWhatToSaySuggestion(callId, trigger, replyToSave, nowISO)
                  .catch((err) => console.error('[PERSIST] WhatToSay save failed', err));
              } catch (err) {
                console.error('[PERSIST] WhatToSay persistence threw', err);
              }
            }
          },
          onOpen: () => {
            set((state: any) => ({
              salesCallStatus: {
                ...state.salesCallStatus,
                agentConnections: {
                  ...state.salesCallStatus.agentConnections,
                  whatToResponse: true,
                },
              },
            }));
          },
          onClose: () => {
            set((state: any) => ({
              salesCallStatus: {
                ...state.salesCallStatus,
                agentConnections: {
                  ...state.salesCallStatus.agentConnections,
                  whatToResponse: false,
                },
              },
              urgentHelpInFlight: false,
              objectionInFlight: false,
            }));
          },
        });
      } catch (error) {
        console.error('[SALES] Failed to connect to WhatToResponse agent:', error);
      }

      // Connect to Checklist detector agent for real-time question answers
      try {
        console.log('[CHECKLIST] 📡 Connecting to checklist WebSocket...');
        sdk.sales.wsChecklist({
          onMessage: (data: any) => {
            console.log('[CHECKLIST] 📥 Received event:', data);

            if (data.type === 'ChecklistQuestionAnswered') {
              console.log('[CHECKLIST] ✅ Question answered:', data.questionIndex, 'id:', data.questionId);

              // Emit custom event that the checklist sidebar can listen to
              window.dispatchEvent(new CustomEvent('checklist-question-answered', {
                detail: {
                  questionIndex: data.questionIndex,
                  questionId: data.questionId
                }
              }));
            }
          },
          onOpen: () => {
            console.log('[CHECKLIST] ✅ WebSocket connected');
          },
          onClose: () => {
            console.log('[CHECKLIST] ❌ WebSocket disconnected');
          },
          onError: (error: any) => {
            console.error('[CHECKLIST] ❌ WebSocket error:', error);
          },
        });
      } catch (error) {
        console.error('[CHECKLIST] Failed to connect to checklist agent:', error);
      }

      // Connect to transcription WebSockets for both devices
      try {
        sdk.transcription.connect(salesmanMicId, {
          onTranscription: (data: any) => {
            // Dispatch clearance event on valid speech
            window.dispatchEvent(new CustomEvent('audio-silence-cleared'));

            const newMessage = {
              id: `${salesmanMicId}-${Date.now()}`,
              deviceId: salesmanMicId,
              text: data.text || data.Text || '',
              timestamp: new Date(),
            };
            set((state: any) => {
              const liveTranscriptions = { ...(state.transcriptionStatus.liveTranscriptions || {}) };
              delete liveTranscriptions[salesmanMicId];

              return {
                transcriptionStatus: {
                  ...state.transcriptionStatus,
                  isActive: true,
                  audioAlertVisible: false, // Keep store sync just in case, but rely on events
                  lastTranscription: {
                    deviceId: salesmanMicId,
                    text: data.text || data.Text || '',
                    timestamp: new Date(),
                  },
                  liveTranscriptions,
                  transcriptionHistory: [...state.transcriptionStatus.transcriptionHistory, newMessage],
                },
              };
            });
            const latestText = data.text || data.Text || '';
            if (latestText) {
              const callId = get().salesCallStatus.callId;
              if (!callId) {
                console.warn('[PERSIST] Skipping salesman transcript save: missing callId');
              } else {
                try {
                  const author = 'salesman';
                  const nowISO = new Date().toISOString();
                  const text = latestText;
                  callTranscriptMessageService
                    .createTranscriptMessage(callId, text, author, nowISO)
                    .then((res) => { if (!res) console.error('[PERSIST] Salesman transcript save returned null', { callId }); })
                    .catch((err) => console.error('[PERSIST] Salesman transcript save failed', err));
                } catch (err) {
                  console.error('[PERSIST] Salesman transcript persistence threw', err);
                }
              }
            }
          },
          onPartial: (data: any) => {
            window.dispatchEvent(new CustomEvent('audio-silence-cleared'));

            const text = data.text || data.Text || '';
            const timestamp = new Date(data.timestamp || data.Timestamp || data.ts || Date.now());

            set((state: any) => ({
              transcriptionStatus: {
                ...state.transcriptionStatus,
                isActive: true,
                audioAlertVisible: false,
                lastTranscription: {
                  deviceId: salesmanMicId,
                  text,
                  timestamp,
                },
                liveTranscriptions: {
                  ...(state.transcriptionStatus.liveTranscriptions || {}),
                  [salesmanMicId]: {
                    deviceId: salesmanMicId,
                    text,
                    timestamp,
                  },
                },
              },
            }));
          },
          onPartialEnd: () => {
            set((state: any) => {
              const liveTranscriptions = { ...(state.transcriptionStatus.liveTranscriptions || {}) };
              delete liveTranscriptions[salesmanMicId];
              return {
                transcriptionStatus: {
                  ...state.transcriptionStatus,
                  liveTranscriptions,
                },
              };
            });
          },
          onAlert: (data: any) => {
            console.log('[SALES] Salesman device onAlert received', data);
            if (data && (data.error || data.Error) === 'NO_AUDIO_DETECTED') {
              // Global Silence Check: Ignore if *anyone* spoke recently
              const lastTx = get().transcriptionStatus.lastTranscription;
              if (lastTx && lastTx.timestamp) {
                const timeSinceLastTx = Date.now() - new Date(lastTx.timestamp).getTime();
                if (timeSinceLastTx < 5000) {
                  console.log('[SALES] Ignoring silence alert - global speech detected', timeSinceLastTx, 'ms ago');
                  return;
                }
              }

              console.log('[SALES] Dispatching audio-silence-alert event (Global Silence Detected)');
              window.dispatchEvent(new CustomEvent('audio-silence-alert'));

              set((state: any) => ({
                transcriptionStatus: {
                  ...state.transcriptionStatus,
                  audioAlertVisible: true,
                },
              }));
            }
          },
          onOpen: () => {
            set((state: any) => ({
              transcriptionStatus: {
                ...state.transcriptionStatus,
                isActive: true,
                connectedDevices: [...new Set([...state.transcriptionStatus.connectedDevices, salesmanMicId])],
              },
            }));
          },
          onError: (error: any) => {
            console.error('[TRANSCRIPTION] Error connecting to salesman device:', error);
          },
          onClose: () => {
            set((state: any) => {
              const liveTranscriptions = { ...(state.transcriptionStatus.liveTranscriptions || {}) };
              delete liveTranscriptions[salesmanMicId];

              return {
                transcriptionStatus: {
                  ...state.transcriptionStatus,
                  liveTranscriptions,
                  connectedDevices: state.transcriptionStatus.connectedDevices.filter((id: string) => id !== salesmanMicId),
                  isActive: state.transcriptionStatus.connectedDevices.length > 1,
                },
              };
            });
          },
        });
      } catch (error) {
        console.error('[TRANSCRIPTION] Failed to connect to salesman device:', error);
      }

      try {
        sdk.transcription.connect(clientMicId, {
          onTranscription: (data: any) => {
            // Dispatch clearance event on valid speech
            window.dispatchEvent(new CustomEvent('audio-silence-cleared'));

            const newMessage = {
              id: `${clientMicId}-${Date.now()}`,
              deviceId: clientMicId,
              text: data.text || data.Text || '',
              timestamp: new Date(),
            };
            set((state: any) => {
              const liveTranscriptions = { ...(state.transcriptionStatus.liveTranscriptions || {}) };
              delete liveTranscriptions[clientMicId];

              return {
                transcriptionStatus: {
                  ...state.transcriptionStatus,
                  isActive: true,
                  audioAlertVisible: false, // Keep store sync just in case, but rely on events
                  lastTranscription: {
                    deviceId: clientMicId,
                    text: data.text || data.Text || '',
                    timestamp: new Date(),
                  },
                  liveTranscriptions,
                  transcriptionHistory: [...state.transcriptionStatus.transcriptionHistory, newMessage],
                },
              };
            });
            const latestText = data.text || data.Text || '';
            if (latestText) {
              const callId = get().salesCallStatus.callId;
              if (!callId) {
                console.warn('[PERSIST] Skipping client transcript save: missing callId');
              } else {
                try {
                  const author = 'client';
                  const nowISO = new Date().toISOString();
                  const text = latestText;
                  callTranscriptMessageService
                    .createTranscriptMessage(callId, text, author, nowISO)
                    .then((res) => { if (!res) console.error('[PERSIST] Client transcript save returned null', { callId }); })
                    .catch((err) => console.error('[PERSIST] Client transcript save failed', err));
                } catch (err) {
                  console.error('[PERSIST] Client transcript persistence threw', err);
                }
              }
            }
          },
          onPartial: (data: any) => {
            window.dispatchEvent(new CustomEvent('audio-silence-cleared'));

            const text = data.text || data.Text || '';
            const timestamp = new Date(data.timestamp || data.Timestamp || data.ts || Date.now());

            set((state: any) => ({
              transcriptionStatus: {
                ...state.transcriptionStatus,
                isActive: true,
                audioAlertVisible: false,
                lastTranscription: {
                  deviceId: clientMicId,
                  text,
                  timestamp,
                },
                liveTranscriptions: {
                  ...(state.transcriptionStatus.liveTranscriptions || {}),
                  [clientMicId]: {
                    deviceId: clientMicId,
                    text,
                    timestamp,
                  },
                },
              },
            }));
          },
          onPartialEnd: () => {
            set((state: any) => {
              const liveTranscriptions = { ...(state.transcriptionStatus.liveTranscriptions || {}) };
              delete liveTranscriptions[clientMicId];
              return {
                transcriptionStatus: {
                  ...state.transcriptionStatus,
                  liveTranscriptions,
                },
              };
            });
          },
          onAlert: (data: any) => {
            console.log('[SALES] Client device onAlert received', data);
            if (data && (data.error || data.Error) === 'NO_AUDIO_DETECTED') {
              // Global Silence Check: Ignore if *anyone* spoke recently
              const lastTx = get().transcriptionStatus.lastTranscription;
              if (lastTx && lastTx.timestamp) {
                const timeSinceLastTx = Date.now() - new Date(lastTx.timestamp).getTime();
                if (timeSinceLastTx < 5000) {
                  console.log('[SALES] Ignoring silence alert - global speech detected', timeSinceLastTx, 'ms ago');
                  return;
                }
              }

              console.log('[SALES] Dispatching audio-silence-alert event (Global Silence Detected)');
              window.dispatchEvent(new CustomEvent('audio-silence-alert'));

              set((state: any) => ({
                transcriptionStatus: {
                  ...state.transcriptionStatus,
                  audioAlertVisible: true,
                },
              }));
            }
          },
          onOpen: () => {
            set((state: any) => ({
              transcriptionStatus: {
                ...state.transcriptionStatus,
                isActive: true,
                connectedDevices: [...new Set([...state.transcriptionStatus.connectedDevices, clientMicId])],
              },
            }));
          },
          onError: (error: any) => {
            console.error('[TRANSCRIPTION] Error connecting to client device:', error);
          },
          onClose: () => {
            set((state: any) => {
              const liveTranscriptions = { ...(state.transcriptionStatus.liveTranscriptions || {}) };
              delete liveTranscriptions[clientMicId];

              return {
                transcriptionStatus: {
                  ...state.transcriptionStatus,
                  liveTranscriptions,
                  connectedDevices: state.transcriptionStatus.connectedDevices.filter((id: string) => id !== clientMicId),
                  isActive: state.transcriptionStatus.connectedDevices.length > 1,
                },
              };
            });
          },
        });
      } catch (error) {
        console.error('[TRANSCRIPTION] Failed to connect to client device:', error);
      }

      set((state: any) => ({
        salesCallStatus: {
          ...state.salesCallStatus,
          isActive: true,
          isPaused: false,
          pauseStartedAt: null,
          accumulatedPauseMs: 0,
          salesmanMicId,
          clientMicId,
          startTime: new Date(),
          simulatorStatus: callMode === 'simulator' ? 'reconnecting' : null,
          agentConnections,
          agentData,
        },
        salesLoading: false,
        salesError: null,
      }));

      registerStopOnUnload(get);
      startAutoDisconnectTimer(
        () => get().salesCallStatus?.isActive,
        get().stopSalesCall,
        get().salesCallStatus.startTime ?? undefined
      );
    } catch (error) {
      const errMsg = (error as any)?.data?.error
        ?? (error instanceof Error ? error.message : 'Failed to start sales call');
      set((state: any) => ({
        salesLoading: false,
        salesError: errMsg,
        salesCallStatus: {
          ...state.salesCallStatus,
          simulatorStatus: callMode === 'simulator' ? 'error' : state.salesCallStatus.simulatorStatus,
        },
      }));
    }
  },

  stopSalesCall: async () => {
    const { sdk, salesCallStatus, salesLoading, connectionStatus } = get();
    if (salesLoading) {
      console.info('[SALES] stopSalesCall ignored: operation already in progress');
      return;
    }

    if (!sdk || !salesCallStatus.isActive) {
      if (!salesCallStatus.isActive) console.info('[SALES] stopSalesCall ignored: no active call');
      return;
    }

    clearAutoDisconnectTimer();
    const stopEndpointPath = salesCallStatus.callMode === 'simulator' ? '/sales/stop-simulator-call' : '/sales/stop-sale-call';

    // Edge case: if server is disconnected, we can't properly call stopSaleCall() or do summaries.
    // We should still stop the call on the frontend to avoid getting stuck.
    set({ salesLoading: true, isProcessingSummary: true });

    try {
      // Clear log context
      logService.clearCallContext();

      // If connected, try standard WS stop. Otherwise fallback to HTTP
      if (connectionStatus.isConnected) {
        try {
          const res = salesCallStatus.callMode === 'simulator' && sdk.sales.stopSimulatorCall
            ? await sdk.sales.stopSimulatorCall()
            : await sdk.sales.stopSaleCall();
          console.log('[SALES] stop call response:', res);
          // Check for feedback result in the response
          if (res && res.feedback_result) {
            console.log('[SALES] Received feedback result immediately from stop call:', res.feedback_result);
            set((state: any) => ({
              salesCallStatus: {
                ...state.salesCallStatus,
                agentData: {
                  ...state.salesCallStatus.agentData,
                  feedbackResult: res.feedback_result,
                  isFeedbackStreaming: false, // Got Full Result
                  feedbackCallId: state.salesCallStatus.callId,
                  isSummaryStreaming: false,
                  isWhatsappStreaming: false,
                  isAnalysisStreaming: false,
                  isAnalysisRequested: false,
                  callDurationSeconds: typeof res.call_duration === 'number' ? res.call_duration : state.salesCallStatus.agentData?.callDurationSeconds ?? null,
                }
              }
            }));
          } else if (res && res.feedback_enqueued) {
            // No result yet -> Enable Streaming
            console.log('[SALES] Feedback enqueued, enabling streaming for call:', salesCallStatus.callId);
            set((state: any) => ({
              salesCallStatus: {
                ...state.salesCallStatus,
                agentData: {
                  ...state.salesCallStatus.agentData,
                  isFeedbackStreaming: true,
                  feedbackCallId: state.salesCallStatus.callId,
                  feedbackResult: null, // Clear previous
                  isSummaryStreaming: false,
                  isWhatsappStreaming: false,
                  isAnalysisStreaming: false,
                  isAnalysisRequested: false,
                  callDurationSeconds: typeof res.call_duration === 'number' ? res.call_duration : state.salesCallStatus.agentData?.callDurationSeconds ?? null,
                }
              }
            }));
          } else {
            console.log('[SALES] Feedback generation skipped (call duration < 30s or other reason)');
            // Ensure streaming is OFF but preserve feedbackCallId for manual trigger
            set((state: any) => ({
              salesCallStatus: {
                ...state.salesCallStatus,
                agentData: {
                  ...state.salesCallStatus.agentData,
                  isFeedbackStreaming: false,
                  feedbackCallId: state.salesCallStatus.callId,
                  feedbackResult: null,
                  isSummaryStreaming: false,
                  isWhatsappStreaming: false,
                  isAnalysisStreaming: false,
                  isAnalysisRequested: false,
                  callDurationSeconds: typeof res?.call_duration === 'number' ? res.call_duration : state.salesCallStatus.agentData?.callDurationSeconds ?? null,
                }
              }
            }));
          }
        } catch (e) {
          console.warn('[SALES] Stop call API failed, proceeding with local cleanup:', e);
        }
      } else {
        console.warn('[SALES] Stopping call while server is disconnected. Forcing stop via HTTP fallback...');
        const baseUrl = sdk._cfg?.baseURL || 'http://localhost:8080';
        try {
          const res = await fetch(`${baseUrl}${stopEndpointPath}`, {
            method: 'POST',
            keepalive: true,
            headers: { 'Content-Type': 'application/json' },
            body: ''
          });

          if (res.ok) {
            const data = await res.json();
            if (data && data.feedback_result) {
              console.log('[SALES] Received feedback result from HTTP fallback:', data.feedback_result);
              set((state: any) => ({
                salesCallStatus: {
                  ...state.salesCallStatus,
                  agentData: {
                    ...state.salesCallStatus.agentData,
                    feedbackResult: data.feedback_result,
                    isFeedbackStreaming: false,
                    feedbackCallId: state.salesCallStatus.callId,
                    isSummaryStreaming: false,
                    isWhatsappStreaming: false,
                    isAnalysisStreaming: false,
                    isAnalysisRequested: false,
                    callDurationSeconds: typeof data.call_duration === 'number' ? data.call_duration : state.salesCallStatus.agentData?.callDurationSeconds ?? null,
                  }
                }
              }));
            } else {
              set((state: any) => ({
                salesCallStatus: {
                  ...state.salesCallStatus,
                  agentData: {
                    ...state.salesCallStatus.agentData,
                    isFeedbackStreaming: false,
                    feedbackCallId: state.salesCallStatus.callId,
                    feedbackResult: null,
                    isSummaryStreaming: false,
                    isWhatsappStreaming: false,
                    isAnalysisStreaming: false,
                    isAnalysisRequested: false,
                    callDurationSeconds: typeof data?.call_duration === 'number' ? data.call_duration : state.salesCallStatus.agentData?.callDurationSeconds ?? null,
                  }
                }
              }));
            }
          }
        } catch (e) {
          console.warn('[SALES] HTTP stop fallback failed:', e);
        }
      }

      try {
        const callId = salesCallStatus.callId;
        if (!callId) {
          console.error('[PERSIST] Cannot end call: missing callId');
        } else {
          callService
            .endCall(callId)
            .then((res) => { if (!res) console.error('[PERSIST] Call end update returned null', { callId }); else console.info('[PERSIST] Call ended', { callId }); })
            .catch((e) => console.error('[PERSIST] Failed to persist call end:', e));
        }
      } catch (e) {
        console.error('[PERSIST] Exception during call end:', e);
      }

      // Wait for any final messages (e.g. manual summary) to arrive before closing sockets
      // This fix ensures the "final manual summary" triggered by backend on stop has time to reach the frontend
      await new Promise(resolve => setTimeout(resolve, 2500));

      WSClient.closeAll();
      unregisterStopOnUnload();

      try {
        await sdk.devices.refresh();
      } catch (e) {
        console.warn('[STORE] Device refresh failed, continuing with fetch:', e);
      }

      try {
        const devicesPromise = sdk.devices.listAll();
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Device fetch timeout after 10 seconds')), 10000)
        );

        const devices = await Promise.race([devicesPromise, timeoutPromise]) as any[];
        const processedDevices = devices.map((device: any, index: number) => ({
          id: device.HexID || `device-${index}`,
          name: device.Name || `Device ${index + 1}`,
          type: device.Type || 'unknown',
          status: 'active' as const,
        }));
        set({ devices: processedDevices, devicesLoading: false, devicesError: null });
      } catch (e) {
        console.error('[STORE] Failed to fetch devices after stop:', e);
        set({ devicesLoading: false, devicesError: e instanceof Error ? e.message : 'Failed to fetch devices' });
      }

      // Do not clear conversation data on stop; only mark call as inactive.
      // Preserve transcription history, lastTranscription, device IDs, and agent data
      set((state: any) => ({
        transcriptionStatus: {
          ...state.transcriptionStatus,
          isActive: false,
        },
      }));

      set((state: any) => ({
        salesCallStatus: {
          ...state.salesCallStatus,
          isActive: false,
          isPaused: false,
          pauseStartedAt: null,
          accumulatedPauseMs: 0,
          callId: null,
          simulatorStatus: null,
          agentData: {
            ...state.salesCallStatus.agentData,
            // Robustly preserve callId for feedback/analysis
            feedbackCallId: state.salesCallStatus.callId || state.salesCallStatus.agentData.feedbackCallId,
          }
        },
        salesLoading: false,
        salesError: null,
        urgentHelpInFlight: false,
        objectionInFlight: false,
        isProcessingSummary: false,
      }));
    } catch (error) {
      set({
        salesLoading: false,
        isProcessingSummary: false,

        salesError: error instanceof Error ? error.message : 'Failed to stop sales call',
      });
    }
  },

  pauseSalesCall: async () => {
    const { sdk, salesCallStatus } = get();
    if (!sdk || !salesCallStatus.isActive || salesCallStatus.isPaused) return;

    try {
      if (sdk.sales.pauseSaleCall) {
        await sdk.sales.pauseSaleCall();
      }
      set((state: any) => ({
        salesCallStatus: {
          ...state.salesCallStatus,
          isPaused: true,
          pauseStartedAt: state.salesCallStatus.pauseStartedAt || new Date().toISOString(),
        },
      }));
      console.info('[SALES] Call paused');
    } catch (e) {
      console.error('[SALES] Failed to pause call:', e);
    }
  },

  resumeSalesCall: async () => {
    const { sdk, salesCallStatus } = get();
    if (!sdk || !salesCallStatus.isActive || !salesCallStatus.isPaused) return;

    try {
      if (sdk.sales.resumeSaleCall) {
        await sdk.sales.resumeSaleCall();
      }
      set((state: any) => ({
        salesCallStatus: {
          ...state.salesCallStatus,
          isPaused: false,
          pauseStartedAt: null,
          accumulatedPauseMs: state.salesCallStatus.accumulatedPauseMs + (
            state.salesCallStatus.pauseStartedAt
              ? Math.max(0, Date.now() - new Date(state.salesCallStatus.pauseStartedAt).getTime())
              : 0
          ),
        },
      }));
      console.info('[SALES] Call resumed');
    } catch (e) {
      console.error('[SALES] Failed to resume call:', e);
    }
  },
});
